import { useEffect, useState } from 'react';

import { TextField, MenuItem, Stack, CircularProgress, Typography, Switch } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { ActionDialog } from '../../../../components';
import * as actions from '../../../../store/actions';
import { useStyles } from '../style';

interface UnlinkDialogProps {
	open?: boolean;
	onClose?(...args: unknown[]): unknown;
	data?: object;
	onUnlinkItemGroup?(...args: unknown[]): unknown;
	onUnlinkAllItemGroup?(...args: unknown[]): unknown;
	onResetStateCondition?(...args: unknown[]): unknown;
	unlinkItemGroup?: {
		success?: boolean;
		loading?: boolean;
		error?: object | string;
	};
	unlinkAllItemGroup?: {
		success?: boolean;
		loading?: boolean;
		error?: object | string;
	};
}

const UnlinkDialog = (props: UnlinkDialogProps) => {
	const {
		open,
		onClose,
		data,

		onUnlinkItemGroup,
		unlinkItemGroup,
		onUnlinkAllItemGroup,
		unlinkAllItemGroup,
		onResetStateCondition,
	} = props;
	const { t } = useTranslation();

	const classes = useStyles();

	const { enqueueSnackbar } = useSnackbar();

	const {
		success: unlinkItemGroupSuccess,
		loading: unlinkItemGroupLoading,
		error: unlinkItemGroupError,
	} = unlinkItemGroup;

	const {
		success: unlinkAllItemGroupSuccess,
		loading: unlinkAllItemGroupLoading,
		error: unlinkAllItemGroupError,
	} = unlinkAllItemGroup;

	const [selectItem, setSelectItem] = useState('');

	const [checkedAll, setCheckedAll] = useState(false);

	const handleCloseUnlinkDialog = () => {
		onClose();
		setSelectItem('');
		setCheckedAll(false);
	};

	const handleUnlinkAll = () => {
		setCheckedAll(!checkedAll);
		if (checkedAll) {
			setSelectItem(() => '');
		}
	};

	useEffect(() => {
		if (checkedAll) {
			return setSelectItem(() => '');
		}
		setSelectItem(() => data?.items?.[0].id);
	}, [data, checkedAll]);

	useEffect(() => {
		if (unlinkItemGroupSuccess || unlinkAllItemGroupSuccess) {
			enqueueSnackbar(t('views.unlinkDialog.success.message'), { variant: 'success' });
			onResetStateCondition('unlinkItemGroup', false);
			onResetStateCondition('unlinkAllItemGroup', false);
			handleCloseUnlinkDialog();
		} else if (unlinkItemGroupError || unlinkAllItemGroupError) {
			enqueueSnackbar(t('views.unlinkDialog.error.message'), { variant: 'error' });
			onResetStateCondition('unlinkItemGroup', false);
			onResetStateCondition('unlinkAllItemGroup', false);
		}
	}, [unlinkItemGroup, unlinkAllItemGroup]);

	const handleConfirm = () => {
		if (checkedAll) {
			return onUnlinkAllItemGroup(data.deviceId, data.organisationReference.id);
		}
		onUnlinkItemGroup(data.deviceId, data.organisationReference.id, selectItem);
	};

	const handleSelect = (event) => {
		const id = event.target.value;
		setSelectItem(id);
	};

	const selectProps = {
		name: 'tagType',
		onChange: handleSelect,
		value: selectItem || '',
		InputLabelProps: { shrink: true },
		label: t('views.devices.linkDevices.label.select'),
		disabled: checkedAll,
		select: true,
		size: 'medium',
		fullWidth: true,
		variant: 'outlined',
	};

	return (
		<ActionDialog
			actionButtonProps={{
				action: handleConfirm,
				text:
					unlinkItemGroupLoading || unlinkAllItemGroupLoading ?
						<CircularProgress disableShrink size={24} />
					:	t('ui.confirm'),
			}}
			handleClose={handleCloseUnlinkDialog}
			loading={false}
			open={open}
			title={t('ui.header.unlink.itemGroup')}
		>
			<Stack mt={2} gap={2}>
				<div className={classes.allSwitchCard}>
					<Typography>{t('views.devices.linkDevices.button.unlinkAll')}</Typography>
					<Switch checked={checkedAll} onChange={handleUnlinkAll} />
				</div>
				<TextField {...selectProps}>
					{data?.items ?
						data?.items.map((item) => (
							<MenuItem key={item.id} value={item.id}>
								{item.name}
							</MenuItem>
						))
					:	<MenuItem></MenuItem>}
				</TextField>
			</Stack>
		</ActionDialog>
	);
};

const mapStateToProps = (state) => {
	return {
		unlinkItemGroup: state.condition.unlinkItemGroup,
		unlinkAllItemGroup: state.condition.unlinkAllItemGroup,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onUnlinkItemGroup: (deviceId, organisationId, itemId) =>
			dispatch(actions.unlinkItemGroup(deviceId, organisationId, itemId)),
		onUnlinkAllItemGroup: (deviceId, organisationId) =>
			dispatch(actions.unlinkAllItemGroup(deviceId, organisationId)),
		onResetStateCondition: (state, value) => dispatch(actions.resetStateCondition(state, value)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(UnlinkDialog);
